// foldChangeBarChart.jsx
import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../../theme/ThemeContext';

export default function FoldChangeBarChart({ foldChangeData }) {
    const containerRef = useRef();
    const svgRef = useRef();
    const { theme } = useTheme();

    const drawChart = () => {
        if (!foldChangeData || foldChangeData.length === 0 || !containerRef.current) return;

        // clear previous content
        d3.select(svgRef.current).selectAll('*').remove();

        // get container dimensions
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerWidth * 0.6; // maintain aspect ratio

        const margin = {
            top: containerHeight * 0.05,
            right: containerWidth * 0.05,
            bottom: containerHeight * 0.25,
            left: containerWidth * 0.1
        };

        const innerWidth = containerWidth - margin.left - margin.right;
        const innerHeight = containerHeight - margin.top - margin.bottom;

        // update SVG dimensions
        const svg = d3.select(svgRef.current)
            .attr('width', containerWidth)
            .attr('height', containerHeight);

        // clear and recreate the main group
        svg.selectAll('g').remove();
        const g = svg.append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        const negativeLog2fc = foldChangeData
            // rest of your existing chart drawing code...
            .filter(d => d.log2fc < 0)
            .sort((a, b) => a.log2fc - b.log2fc)
            .slice(0, 5);

        const positiveLog2fc = foldChangeData
            .filter(d => d.log2fc > 0)
            .sort((a, b) => b.log2fc - a.log2fc)
            .slice(0, 5);

        const combinedData = [...negativeLog2fc, ...positiveLog2fc.reverse()];

        const xScale = d3.scaleBand()
            .domain(combinedData.map(d => d.name))
            .range([0, innerWidth])
            .padding(0.1);

        const yScale = d3.scaleLinear()
            .domain([d3.min(combinedData, d => d.log2fc) - 1, d3.max(combinedData, d => d.log2fc) + 1])
            .nice()
            .range([innerHeight, 0]);

        const colorScale = d3.scaleOrdinal()
            .domain(['negative', 'positive'])
            // .range(['#DC143C', '#228B22']);
            // .range([theme.colors.primary, theme.colors.secondary]);
            .range(['rgb(233, 150, 122)', 'rgb(46, 139, 87)']);


        // bars
        g.selectAll('.bar')
            .data(combinedData)
            .join('rect')
            .attr('class', 'bar')
            .attr('x', d => xScale(d.name))
            .attr('y', d => d.log2fc > 0 ? yScale(d.log2fc) : yScale(0))
            .attr('width', xScale.bandwidth())
            .attr('height', d => Math.abs(yScale(d.log2fc) - yScale(0)))
            .attr('fill', d => d.log2fc < 0 ? colorScale('negative') : colorScale('positive'))
            .style('opacity', 0.8);

        // axes
        g.append('g')
            .attr('transform', `translate(0,${yScale(0)})`)
            .call(d3.axisBottom(xScale))
            .attr('color', theme.colors.text)
            .attr('font-family', 'Arial')
            .attr('font-size', '12px')
            .selectAll('text')
            // .attr('transform', 'rotate(-80)')
            .attr('transform', 'rotate(-90)')
            .attr('x', -10)
            .attr('y', -5)
            .style('text-anchor', 'end')
            .style('fill', theme.colors.text);

        g.append('g')
            .call(d3.axisLeft(yScale))
            .attr('color', theme.colors.text)
            .attr('font-family', 'Arial')
            .attr('font-size', '12px')
            .selectAll('text')
            .style('fill', theme.colors.text);

        // labels
        g.append('text')
            .attr('transform', `translate(${innerWidth / 2}, ${innerHeight + margin.bottom - 0})`)
            .attr('font-family', 'Arial')
            .attr('font-size', '12px')
            .style('text-anchor', 'middle')
            .text('Taxa')
            .style('fill', theme.colors.text);

        g.append('text')
            .attr('transform', 'rotate(-90)')
            .attr('y', 0 - margin.left + 0)
            .attr('x', 0 - (innerHeight / 2))
            .attr('x', 0 - (innerHeight / 1.5))
            .attr('dy', '1em')
            .attr('font-family', 'Arial')
            .attr('font-size', '9px')
            .style('text-anchor', 'middle')
            // .text('log2 Fold Change (avg sub-sample / avg total)')
            .text('log2 Fold Change (avg relative abundance sub-sample / avg total relative abundance)')
            .style('fill', theme.colors.text);
    };

    // initial render and resize handling
    useEffect(() => {
        drawChart();

        const handleResize = () => {
            drawChart();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [foldChangeData, theme]);

    return (
        <div ref={containerRef} style={{ width: '100%', height: 'auto' }}>
            <svg
                ref={svgRef}
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'block'
                }}
                preserveAspectRatio="xMidYMid meet"
            />
        </div>
    );
}